import { setNavIndex, restoreNavState, setNavSkipPush, pushNavEntry, replaceTopNavEntry } from '../slices/appSlice';
import { restoreDjpalSnapshot, setActiveSearch, setSearchAllQuery, setSearchAllScope, setArtistsGridContext, setLabelResults, appendLabelTracks, DJPAL_SNAPSHOT_KEYS } from '../slices/djpalSlice';
import { setActiveTab } from '../slices/appSlice';
import { setSidebarOpen as setPlaylistSidebarOpen } from '../slices/playlistSlice';
import { setLikesSidebarOpen } from '../slices/likesSlice';
import { setFollowedArtistsSidebarOpen } from '../slices/followedArtistsSlice';
import { setRekordboxSidebarOpen } from '../slices/rekordboxSlice';
import { setSeratoSidebarOpen } from '../slices/seratoSlice';
import { setTraktorSidebarOpen } from '../slices/traktorSlice';
import { setVirtualDJSidebarOpen } from '../slices/virtualdjSlice';

import { setSubTab as setFeedSubTab } from '../slices/feedSlice';
import { closeSet } from '../slices/automixSlice';
import { requestShowPlaylist } from '../../utils/playlistOpenRequest';
import { openAutomixSet } from './automixThunk';
import { ensureToken } from './djpalThunk';
import { track } from '../../utils/analytics';

// DESIGN V2 — the collection / DJ-software panels are PAGES: they sit in the
// navigation history (back/forward restores them) and any tab navigation
// leaves them. `panel` on a nav entry names the page that was open on top of
// `tab`; no `panel` means the bare tab.
const V2_PANEL_SETTERS = {
    playlists: setPlaylistSidebarOpen, likes: setLikesSidebarOpen, artists: setFollowedArtistsSidebarOpen,
    rekordbox: setRekordboxSidebarOpen, serato: setSeratoSidebarOpen, traktor: setTraktorSidebarOpen, virtualdj: setVirtualDJSidebarOpen,
};
export const isDesignV2 = () => typeof document !== 'undefined' && document.documentElement?.dataset?.design === 'v2';
export const v2OpenPanels = (state) => ({
    playlists: !!state.playlist?.sidebarOpen, likes: !!state.likes?.sidebarOpen, artists: !!state.followedArtists?.sidebarOpen,
    rekordbox: !!state.rekordbox?.sidebarOpen, serato: !!state.serato?.sidebarOpen, traktor: !!state.traktor?.sidebarOpen, virtualdj: !!state.virtualdj?.sidebarOpen,
});
export const applyV2Panel = (key) => (dispatch, getState) => {
    const open = v2OpenPanels(getState());
    for (const k of Object.keys(V2_PANEL_SETTERS)) {
        const want = k === key;
        if (open[k] !== want) dispatch(V2_PANEL_SETTERS[k](want));
    }
};

const RESULT_FIELD_BY_KIND = {
    artist: 'artistResults',
    track: 'trackResults',
    mix: 'mixResults',
    trackList: 'trackListResults',
};

// Fields that "belong" to each Mixpedia sub-tab. Selective restore touches
// only these on back/forward, so navigating to the artist tab doesn't wipe
// the track tab's data (and vice versa). Sub-tabs are persistent surfaces
// in the user's mental model.
const KIND_FIELDS = {
    all:       ['searchAllQuery', 'searchAllScope'],
    artist:    ['artistQuery', 'artistResults'],
    track:     ['trackQuery', 'trackResults', 'trackVariant', 'trackVariantCache'],
    mix:       ['mixQuery', 'mixResults', 'mixResultsPagination', 'mixFilterContext', 'expandedMixIds'],
    trackList: ['trackListQuery', 'trackListResults', 'trackListPagination', 'trackListContext'],
    artistsGrid: ['artistsGridContext'],
    label:     ['labelResults'],
};

const restoreEntrySelective = (entryDjpal) => {
    const kind = entryDjpal.activeSearch;
    const fields = KIND_FIELDS[kind] || [];
    const partial = { activeSearch: kind };
    for (const f of fields) partial[f] = entryDjpal[f];
    return partial;
};

// Mixpedia sub-tab clicks (Artists/Tracks/Mixes) feel like browser tabs to
// users — clicking back to a tab should restore what was last there. State
// alone doesn't carry that across back-navigation (each nav snapshot only
// holds the state at result-arrival time, so an artist entry pushed BEFORE a
// track was clicked has trackResults=null). Walk the nav stack to find the
// most recent matching entry and jump to it; fall back to a plain switch
// when nothing's in history.
//
// `kind` may be a single string ('artist' / 'track' / 'mix' / 'trackList')
// or an array of kinds — the Tracks tab passes ['track', 'trackList'] so it
// can restore whichever flavor the user last saw under that tab.
export const switchSubTab = (kindOrKinds) => (dispatch, getState) => {
    const kinds = Array.isArray(kindOrKinds) ? kindOrKinds : [kindOrKinds];
    const { app } = getState();

    // STACK_HIT: forward-only — drilldowns from a back-nav live forward of
    // navIndex. Walking backward would surface entries from before the
    // current branch (a prior artist's track view, etc.) which is confusing.
    for (let i = app.navIndex + 1; i < app.navStack.length; i++) {
        const entry = app.navStack[i];
        if (!entry || entry.tab !== 'djpal' || !entry.djpal) continue;
        for (const kind of kinds) {
            const resultField = RESULT_FIELD_BY_KIND[kind];
            if (!resultField || entry.djpal[resultField] == null) continue;
            dispatch(restoreNavState({ category: entry.category, tab: entry.tab }));
            // Selective restore so the other sub-tabs keep whatever state
            // they currently hold.
            dispatch(restoreDjpalSnapshot(restoreEntrySelective({ ...entry.djpal, activeSearch: kind })));
            dispatch(setNavIndex(i));
            return;
        }
    }

    // No forward match. Switch the active sub-tab and push a nav entry that
    // captures live state. Two reasons: (1) tabs are persistent surfaces — if
    // state already has data for the kind (carried over from a previous view),
    // the switched-to tab should show that data, NOT empty. (2) pushing a nav
    // entry means clicking back returns to the previous Mixpedia state instead
    // of escaping to the category landing page.
    const newKind = kinds[0];
    dispatch(setActiveSearch(newKind));
    const djpalAfter = getState().djpal;
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpalAfter[key];
    dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snap }));
};

// restoreNavState and restoreDjpalSnapshot are not listened to by navHistoryMiddleware,
// so no skipPush is needed for them. setFeedSubTab IS listened to, though, so we
// raise the skip flag for that one dispatch to keep the stack from growing on restore.
// Open one of the Mixpedia home's full-page "Show more" views
// ('homeSets' | 'homeTracks'). These pages have no results-set action for the
// nav middleware to hook, so the nav entry is pushed here explicitly — the
// snapshot captures activeSearch, which is all the page needs to restore.
export const openMixpediaBrowse = (kind) => (dispatch, getState) => {
    track('browse_opened', { row: kind });
    dispatch(setActiveSearch(kind));
    const { djpal } = getState();
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
    dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snap }));
};

// Open the full-page artists grid (homepage-style avatar+Follow cards) for
// an entity page's artist list — e.g. "Artists supporting them". Same
// explicit nav-entry push as openMixpediaBrowse: the page has no results-set
// action for the nav middleware to hook.
// context: { title, subtitle, artists: [{ name, id, artworkUrl }], from }
export const openMixpediaArtistsGrid = ({ from, ...context }) => (dispatch, getState) => {
    track('browse_opened', { row: 'artistsGrid', from });
    dispatch(setArtistsGridContext(context));
    dispatch(setActiveSearch('artistsGrid'));
    const { djpal } = getState();
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
    dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snap }));
};

const LABEL_PAGE_SIZE = 50;

// Open a label page ('label' view): header + paginated tracks from
// GET /labels/:publicId. The pending header paints at once; the nav entry is
// pushed immediately too (so Back works mid-fetch) and REPLACED with the full
// snapshot when the data lands, so Back/Forward restore the loaded page, not
// a skeleton. No results-set action for the nav middleware to hook here —
// same explicit push as openMixpediaBrowse.
// from: 'trackPage' | 'artistPage' | 'search' | 'labelPage' | ...
export const openMixpediaLabel = ({ publicId, name, from }) => async (dispatch, getState) => {
    if (!publicId) return;
    track('browse_opened', { row: 'label', from, label_id: publicId, label_name: name });
    dispatch(setLabelResults({ publicId, name: name || '', pending: true }));
    dispatch(setActiveSearch('label'));
    const snapshot = () => {
        const { djpal } = getState();
        const snap = {};
        for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
        return snap;
    };
    // From another tab, setActiveTab's middleware pushes the marker (with the
    // pending label state); on Mixpedia already, push it ourselves.
    if (getState().app.activeTab !== 'djpal') {
        dispatch(setActiveTab('djpal'));
    } else {
        dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snapshot() }));
    }

    let next;
    try {
        const token = await ensureToken(dispatch, getState);
        const result = await window.electron.djpal.getLabel(token, publicId, { page: 1, limit: LABEL_PAGE_SIZE });
        if (result?.error || !result?.data) {
            next = { publicId, name: name || '', error: result?.error || 'Label not found' };
        } else {
            const d = result.data;
            next = { ...d, publicId: d.publicId || publicId, name: d.name || name || '', tracks: d.tracks || [], totalTracks: d.totalTracks ?? (d.tracks || []).length, page: d.page || 1, limit: d.limit || LABEL_PAGE_SIZE };
        }
    } catch (err) {
        next = { publicId, name: name || '', error: err?.message || 'Label lookup failed' };
    }
    // The user may have navigated away meanwhile — only the page still
    // waiting on this exact label takes the result.
    const current = getState().djpal.labelResults;
    if (!current || current.publicId !== publicId || !current.pending) return;
    dispatch(setLabelResults(next));
    const { app } = getState();
    const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
    if (top && top.tab === 'djpal' && top.djpal?.activeSearch === 'label' && top.djpal.labelResults?.publicId === publicId) {
        dispatch(replaceTopNavEntry({ ...top, djpal: snapshot() }));
    } else {
        dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snapshot() }));
    }
};

// "Load more" on the label page's tracks list. Appends the next page and
// refreshes the top nav entry so Back/Forward keep the longer list.
export const loadMoreLabelTracks = () => async (dispatch, getState) => {
    const cur = getState().djpal.labelResults;
    if (!cur || cur.pending || cur.error || cur.loadingMore) return;
    const page = (cur.page || 1) + 1;
    const limit = cur.limit || LABEL_PAGE_SIZE;
    dispatch(setLabelResults({ ...cur, loadingMore: true }));
    try {
        const token = await ensureToken(dispatch, getState);
        const result = await window.electron.djpal.getLabel(token, cur.publicId, { page, limit });
        const still = getState().djpal.labelResults;
        if (!still || still.publicId !== cur.publicId) return;
        if (result?.error || !result?.data) {
            dispatch(setLabelResults({ ...still, loadingMore: false }));
            return;
        }
        dispatch(setLabelResults({ ...still, loadingMore: false }));
        dispatch(appendLabelTracks({ tracks: result.data.tracks || [], page: result.data.page || page, totalTracks: result.data.totalTracks }));
    } catch {
        const still = getState().djpal.labelResults;
        if (still && still.publicId === cur.publicId) dispatch(setLabelResults({ ...still, loadingMore: false }));
        return;
    }
    const { app, djpal } = getState();
    const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
    if (top && top.tab === 'djpal' && top.djpal?.activeSearch === 'label' && top.djpal.labelResults?.publicId === cur.publicId) {
        const snap = {};
        for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
        dispatch(replaceTopNavEntry({ ...top, djpal: snap }));
    }
};

// Commit a unified search (navbar Enter / "See all results"). When the user
// is ALREADY on the Mixpedia tab, setActiveTab dedupes the re-selection and
// nothing else pushes — so a second search from a results/entity page never
// entered the back stack, and Back skipped it entirely (searched
// "tomorrowland" from the "koko" flow → Back landed on the koko results,
// 2026-08-05). Push the entry explicitly in that case; when arriving from
// another tab, setActiveTab's middleware push captures the query already.
export const openUnifiedResults = (query) => (dispatch, getState) => {
    dispatch(setActiveSearch('all'));
    dispatch(setSearchAllQuery(query));
    const { app, djpal } = getState();
    if (app.activeTab !== 'djpal') {
        dispatch(setActiveTab('djpal'));
        return;
    }
    // Re-submitting the query already on top is not a navigation.
    const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
    if (top && top.tab === 'djpal' && top.djpal &&
        top.djpal.activeSearch === 'all' && top.djpal.searchAllQuery === query) return;
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
    dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snap }));
};

// Scope-pill click on the unified results page. Pushes a nav entry so
// Back un-scopes instead of jumping off the page entirely.
export const openSearchScope = (scope) => (dispatch, getState) => {
    dispatch(setSearchAllScope(scope));
    const { djpal } = getState();
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
    dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snap }));
};

const LIBRARY_FAMILY = new Set(['library', 'likes', 'playlists', 'followed-artists']);
const tabFamily = (tab) => (LIBRARY_FAMILY.has(tab) ? 'library' : tab);

export const navigateHistory = (direction, { withinTab = false } = {}) => (dispatch, getState) => {
    const { app, djpal } = getState();
    const { navStack, navIndex } = app;
    const target = direction === 'back' ? navIndex - 1 : navIndex + 1;
    if (target < 0 || target >= navStack.length) return false;

    // withinTab (iOS edge swipe): the gesture pops the CURRENT tab's stack
    // only — never switches tabs (SoundCloud/UINavigationController model;
    // the bottom bar is the tab switcher). Android's system back and any
    // explicit back buttons keep the unrestricted cross-tab behavior.
    // Library sub-pages (Likes / Playlists / Favourite Artists) are the
    // Library tab's own stack on phones — swiping back from them lands on
    // the Library hub, not nowhere (they were treated as separate tabs and
    // the gesture silently did nothing, 2026-08-17).
    if (withinTab && tabFamily(navStack[target].tab) !== tabFamily(app.activeTab)) return false;

    // Leaving a clean djpal marker via history nav: capture live state into
    // it, the same refresh navHistoryMiddleware does when leaving Mixpedia
    // through setActiveTab. History moves bypass setActiveTab, so without
    // this the marker kept its stripped snapshot (query set, results null)
    // and restoring it later rendered a Mixpedia page with only the search
    // bar — no results, no home.
    const current = navIndex >= 0 ? navStack[navIndex] : null;
    if (current && current.tab === 'djpal' && current.djpal && app.activeTab === 'djpal') {
        const currentIsClean =
            current.djpal.artistResults == null &&
            current.djpal.trackResults == null &&
            current.djpal.mixResults == null &&
            current.djpal.trackListResults == null;
        if (currentIsClean) {
            const snap = {};
            for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
            dispatch(replaceTopNavEntry({ ...current, djpal: snap }));
        }
    }
    const entry = navStack[target];

    dispatch(restoreNavState({ category: entry.category, tab: entry.tab }));
    if (isDesignV2()) {
        dispatch(applyV2Panel(entry.panel || null));
        // The Playlists page has its own inner navigation (list → playlist):
        // an entry carries the open playlist, or nothing for the list.
        if (entry.panel === 'playlists') requestShowPlaylist(entry.playlist || null);
    }
    if (entry.djpal) {
        // Selective restore — only touch the kind's own fields. Other sub-tabs'
        // state stays put across navigation.
        dispatch(restoreDjpalSnapshot(restoreEntrySelective(entry.djpal)));
    }
    if (entry.tab === 'feed' && entry.feedSubTab) {
        dispatch(setNavSkipPush(true));
        dispatch(setFeedSubTab(entry.feedSubTab));
    }
    // Automix: the set view is a page inside the tab, so back/forward has to
    // open or close it rather than only swapping tabs. Entries pushed before
    // this existed carry no `automix` key — treat them as "no set open", which
    // is what the picker is.
    if (entry.tab === 'automix') {
        const wanted = entry.automix?.setDir || null;
        const open = getState().automix.set?.dir || null;
        if (wanted !== open) {
            if (wanted) dispatch(openAutomixSet(wanted, 'history', { push: false }));
            else dispatch(closeSet());
        }
    }
    dispatch(setNavIndex(target));
    return true;
};

// "Back to sets" inside the set view. When the stop underneath is this same
// tab without a set open, closing IS going back — walk the stack instead of
// only clearing state, or the set entry stays on top and the next Back press
// does nothing visible.
export const closeAutomixSet = () => (dispatch, getState) => {
    const { app } = getState();
    const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
    const under = app.navIndex > 0 ? app.navStack[app.navIndex - 1] : null;
    if (top?.tab === 'automix' && top.automix?.setDir
        && under?.tab === 'automix' && !under.automix?.setDir) {
        return dispatch(navigateHistory('back'));
    }
    dispatch(closeSet());
    return false;
};
